/* eslint-disable max-lines-per-function */
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import $ from "jquery";
import { omit } from "lodash";
import React from "react";
import { RouteComponentProps } from "@reach/router";
import {
  QueryInterestFormData,
  QueryInterestFormDataVariables,
  UpdateInterestFormData,
  UpdateInterestFormDataVariables,
} from "types/gql-types";
import { interestAllFields } from "utils/graphql";
import { parseDumbInterestForm } from "utils/interest";
import { useSnackbar } from "components/haven-design/snackbar";
import { useTablesRef } from "components/table-refs";
import InterestUpdateContent from "./interest-update-content";
import { updateCachedQueries } from "./utils";

const UPDATE_INTEREST_DATA = gql`
  mutation UpdateInterestFormData(
    $interestId: String!
    $lastUpdated: String!
    $data: InterestInput!
    $keepOnList: Boolean
  ) {
    pmUpdateCompletedInterest(
      id: $interestId, 
      lastUpdated: $lastUpdated, 
      updateData: $data, 
      keepOnList: $keepOnList
      ) {
      interest {
        ${interestAllFields}
        updateRequest {
          isActive
        }
      }
      status
    }
  }
`;

export const QUERY_INTEREST_DATA = gql`
  query QueryInterestFormData($interestId: ID!, $interestListId: ID!) {
    applicationFormMetadata(interestListId: $interestListId, source: APP) {
      schema {
        name
        label
        options {
          label
          value
        }
      }
    }

    interest(id: $interestId) {
      ${interestAllFields}

      interestList {
        id
        scheduledUpdate {
          enabled
        }
      }

      updateRequestInterests {
        respondedAt
        updateRequest {
          isActive
        }
      }
    }
  }
`;

interface Props {
  interestID: string;
  interestListID: string;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
}

const InterestUpdatePage: React.FC<RouteComponentProps & Props> = (props) => {
  const { openSnackbar } = useSnackbar();
  const { interestListID, interestID } = props;
  const [submitIndex, setSubmitIndex] = React.useState(0);

  const variables = { interestId: interestID, interestListId: interestListID };
  const query = useQuery<QueryInterestFormData, QueryInterestFormDataVariables>(
    QUERY_INTEREST_DATA,
    {
      fetchPolicy: "network-only",
      variables,
    },
  );

  const { manualContactTableRef, waitlistUpdateTableRef } = useTablesRef();

  const [updateFormData, updateQuery] = useMutation<
  UpdateInterestFormData, UpdateInterestFormDataVariables>(UPDATE_INTEREST_DATA, {
    update: (cache, { data }) => {
      if (data) {
        updateCachedQueries(cache, data, variables);

        [
          manualContactTableRef,
          waitlistUpdateTableRef,
          // @ts-ignore
        ].forEach(ref => ref.current?.onQueryChange());
      }
    },
  });


  if (query.loading) return <span>Loading...</span>;

  if (
    query.error ||
    !(query.data?.applicationFormMetadata?.schema)
  ) {
    return <span>There was a problem loading this page. Please refresh and try again.</span>;
  }

  const {
    data: {
      interest,
      applicationFormMetadata: { schema },
    },
  } = query;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData: any = omit(parseDumbInterestForm($("form#interest-form")[0], interest), "temp-input");
    const adultCount = Number(formData.applicant_adult_count || 0);
    const childCount = Number(formData.applicant_child_count || 0);
    if (adultCount || childCount) {
      // eslint-disable-next-line camelcase
      formData.household_count = adultCount + childCount;
    }

    const updateRequestInterests = interest?.updateRequestInterests || [];
    const requestIsActive = updateRequestInterests
      .some(({ updateRequest: { isActive } }) => isActive);

    updateFormData({
      variables: {
        interestId: props.interestID,
        // @ts-ignore
        lastUpdated: query.data?.interest?.updated_at,
        keepOnList: requestIsActive && submitIndex === 0,
        data: formData,
      },
    })
      .then((result) =>
        props.onSubmit().then(() => {
          window.scrollTo(0, 0);
          const status = result.data?.pmUpdateCompletedInterest.status;
          if (status === "sync") {
            openSnackbar({ message: "Application details updated.", status: "success" });
          } else {
            openSnackbar({ message: "Application details updated. Dashboard will be reflected momentarily.", status: "warning" });
          }
        }))
      .catch((err) => {
        openSnackbar({ message: err.message, status: "error" });
      });
  };

  return (
    <div className="panel panel-default">
      <div className="panel-heading">Application details</div>
      <div className="panel-body">
        <InterestUpdateContent
          isSending={updateQuery.loading}
          formData={query.data.interest}
          schema={schema}
          submitBtnIndex={submitIndex}
          setSubmitBtnIndex={(index: number) => setSubmitIndex(index)}
          onChangeHousehold={(event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
            updateFormData({
              variables: {
                interestId: props.interestID,
                // @ts-ignore
                lastUpdated: query.data?.interest?.updated_at,
                data: { householdCount: event.target.value },
              },
            })
              .then(() => window.location.reload())
              .catch((err) => {
                openSnackbar({ message: err.message, status: "error" });
              });
          }}
          onSubmit={onSubmit}
          onCancel={props.onCancel}
        />
      </div>
    </div>
  );
};

export default InterestUpdatePage;
